'use client';

import Link from "next/link";
import Image from "next/image";
import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, Eye, EyeOff, Lock, Mail, ArrowRight } from "lucide-react";
import SocialLoginButtons from "@/components/auth/SocialLoginButtons";
import { useContactInfo } from "@/context/SiteConfigContext";

// Allow only same-site relative paths to prevent open-redirect attacks
// (e.g. /login?redirect=https://evil.tld). Reject protocol-relative URLs
// like "//evil.tld" which browsers treat as absolute.
function sanitizeRedirect(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  return raw;
}

function LoginForm() {
  useRouter();
  const searchParams = useSearchParams();
  const redirectUrl = sanitizeRedirect(searchParams.get('redirect'));
  const { phone } = useContactInfo();

  const isNewlyRegistered = searchParams.get('registered') === 'true';
  const needsVerification = searchParams.get('verify') === 'true';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let sessionId = null;
      try {
        const guestCart = localStorage.getItem('guest_cart');
        if (guestCart) {
          sessionId = btoa(guestCart).substring(0, 32);
        }
      } catch (e) {}

      const loginUrl = sessionId
        ? `/api/v2/auth/login?sessionId=${encodeURIComponent(sessionId)}`
        : '/api/v2/auth/login';

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.error || 'Login failed';
        setError(
          errorMessage === 'Invalid credentials'
            ? 'Invalid email or password. Please try again.'
            : errorMessage
        );
        setLoading(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      const userRes = await fetch('/api/v2/auth/me', { credentials: 'include' });
      let targetUrl = '/';

      if (userRes.ok) {
        const userResult = await userRes.json();
        const userData = userResult.data || userResult;
        switch (userData.user.role) {
          case 'super_admin': targetUrl = '/super-admin'; break;
          case 'admin': targetUrl = '/admin'; break;
          case 'vendor': targetUrl = '/vendor'; break;
          case 'dealer': targetUrl = '/dealer'; break;
          case 'installer': targetUrl = '/installer'; break;
          case 'shipping_agent': targetUrl = '/shipping'; break;
          case 'customer':
            targetUrl = (redirectUrl && redirectUrl !== '/') ? redirectUrl : '/?login=success';
            break;
          default: targetUrl = '/';
        }
      }

      try {
        localStorage.setItem('auth_changed', Date.now().toString());
        setTimeout(() => localStorage.removeItem('auth_changed'), 100);
      } catch (e) {}

      window.location.replace(targetUrl);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Login error:', err);
      }
      setError(
        err instanceof Error && err.message.includes('fetch')
          ? 'Unable to connect to server. Please check your internet connection.'
          : 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side — Hero image with overlay */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        <Image
          src="/images/hero/login-hero.png"
          alt="Beautiful window blinds"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="space-y-6">
            <h2 className="text-4xl font-bold leading-tight">
              Transform Your Space<br />
              <span className="text-white/80">With Perfect Window Treatments</span>
            </h2>
            <p className="text-lg text-white/70 max-w-md">
              Access your account to manage orders, save favorite configurations, and get exclusive member pricing.
            </p>
            <div className="flex items-center gap-3 pt-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
              <svg className="w-6 h-6 text-white/80 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
              <p className="text-white/90 text-sm font-medium">Contact us today for a <b>complimentary custom quote </b> <a href={`tel:${phone}`} className="underline">{phone}</a></p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side — Login form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <Link href="/" className="inline-flex items-center gap-2 text-gray-900">
              <svg className="w-7 h-7 text-primary-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="4" rx="1" />
                <rect x="3" y="9" width="18" height="4" rx="1" />
                <rect x="3" y="15" width="18" height="4" rx="1" />
              </svg>
              <span className="text-lg font-bold">Smart Blinds™</span>
            </Link>
          </div>

          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome back</h1>
            <p className="mt-2 text-gray-500">
              Sign in to your account to continue
            </p>
          </div>

          {/* Registration success + email verification notice */}
          {isNewlyRegistered && needsVerification && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-800">Account Created! Check Your Email</p>
                <p className="text-sm text-blue-700 mt-1">
                  We&apos;ve sent a verification link to your email address. Please click the link to activate your account before logging in.
                </p>
                <Link href="/verify-email" className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-1 inline-block">
                  Didn&apos;t receive it? Resend verification email
                </Link>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-100">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Social login first */}
          <SocialLoginButtons className="!space-y-3" />

          {/* Form */}
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail className="h-4.5 w-4.5 text-gray-400" size={18} />
                </div>
                <input maxLength={254}
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  placeholder="name@example.com"
                  className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-red/20 focus:border-primary-red transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link href="/forgot-password" className="text-sm text-primary-red hover:text-red-700 font-medium transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="h-4.5 w-4.5 text-gray-400" size={18} />
                </div>
                <input maxLength={80}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-red/20 focus:border-primary-red transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors min-w-11 min-h-11 justify-center"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-red focus:ring-primary-red border-gray-300 rounded transition-colors"
              />
              <label htmlFor="remember-me" className="ml-2.5 text-sm text-gray-600">
                Keep me signed in
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary-red hover:bg-red-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all shadow-lg shadow-red-500/20 hover:shadow-xl hover:shadow-red-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Register link */}
          <p className="text-center text-sm text-gray-500">
            Don&apos;t have an account?{' '}
            <Link href="/register" className="text-primary-red hover:text-red-700 font-semibold transition-colors">
              Create an account
            </Link>
          </p>

          {/* Terms */}
          <p className="text-center text-xs text-gray-400 pt-4">
            By signing in, you agree to our{' '}
            <Link href="/terms" className="underline hover:text-gray-600">Terms of Service</Link>
            {' '}and{' '}
            <Link href="/privacy" className="underline hover:text-gray-600">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary-red" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
